/**
 * Utilitaire de session : rafraîchissement automatique du JWT côté patient.
 *
 * - Décode le payload JWT (sans vérifier la signature) pour lire l'expiration
 * - Si le token expire dans < 24h, appelle /api/auth/refresh pour obtenir un
 *   nouveau token et le stocke dans localStorage
 * - Fournit `startAuthRefresh()` qui démarre un intervalle périodique (1×/h)
 * - Fournit `authFetch(url, options)` : wrapper fetch qui :
 *     • ajoute l'entête Authorization
 *     • en cas de 401, tente une fois de rafraîchir puis rejoue la requête
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'auth_token';
const REFRESH_THRESHOLD_MS = 24 * 60 * 60 * 1000; // rafraîchir si expiration < 24h

function decodePayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    const padded = pad ? base64 + '='.repeat(4 - pad) : base64;
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function getTokenExpMs(token) {
  const payload = decodePayload(token);
  if (!payload || !payload.exp) return 0;
  return payload.exp * 1000;
}

export function isTokenExpired(token) {
  const exp = getTokenExpMs(token);
  return !exp || exp <= Date.now();
}

export function shouldRefreshToken(token) {
  const exp = getTokenExpMs(token);
  if (!exp) return false;
  const remaining = exp - Date.now();
  return remaining > 0 && remaining < REFRESH_THRESHOLD_MS;
}

let refreshInFlight = null;

export async function refreshAccessToken() {
  // Évite les rafraîchissements concurrents
  if (refreshInFlight) return refreshInFlight;
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token || isTokenExpired(token)) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        return data.access_token;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

let refreshIntervalId = null;

/**
 * Démarre un intervalle qui rafraîchit périodiquement le token si nécessaire.
 * À appeler une seule fois au montage de l'App (retourne un cleanup).
 */
export function startAuthRefresh() {
  const tick = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    if (isTokenExpired(token)) {
      localStorage.removeItem(TOKEN_KEY);
      return;
    }
    if (shouldRefreshToken(token)) {
      await refreshAccessToken();
    }
  };

  // 1re vérification immédiate + toutes les heures
  tick();
  refreshIntervalId = setInterval(tick, 60 * 60 * 1000);
  return () => {
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
      refreshIntervalId = null;
    }
  };
}

/**
 * Wrapper fetch qui ajoute Authorization et rejoue une fois en cas de 401.
 */
export async function authFetch(url, options = {}) {
  const withAuth = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return { ...options, headers };
  };

  let res = await fetch(url, withAuth());
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await fetch(url, withAuth());
    }
  }
  return res;
}
